import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ShieldCheck,
  Check,
  Package,
  ArrowLeft,
  Handshake,
  Truck,
  Store,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SimulatedQr } from "@/components/SimulatedQr";
import { DisputePanel } from "@/components/DisputePanel";
import { adImageSrc } from "@/lib/image-src";
import {
  approveOrder,
  cancelOrder,
  createShippingLabel,
  getOrder,
  payOrder,
  sellerAdvance,
  getShipmentTracking,
} from "@/lib/orders.functions";
import {
  deliveryLabel,
  kr,
  orderSteps,
  statusLabel,
  statusTone,
  shipmentStatusLabel,
  type DeliveryMethod,
} from "@/lib/orders";

export const Route = createFileRoute("/_authenticated/order/$id")({
  component: OrderPage,
  head: () => ({
    meta: [
      { title: "Din order – Sellavo" },
      { name: "description", content: "Följ din trygga affär: betalning, frakt och godkännande." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const methodIcon = (m: DeliveryMethod) =>
  m === "parcel_shop" ? Store : m === "courier" ? Truck : Handshake;

function OrderPage() {
  const { id } = Route.useParams();
  const fetchOrder = useServerFn(getOrder);
  const pay = useServerFn(payOrder);
  const label = useServerFn(createShippingLabel);
  const advance = useServerFn(sellerAdvance);
  const approve = useServerFn(approveOrder);
  const cancel = useServerFn(cancelOrder);
  const fetchTracking = useServerFn(getShipmentTracking);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["order", id],
    queryFn: () => fetchOrder({ data: { id } }),
    refetchInterval: 20_000,
  });

  const trackingNumber = ((data?.order as any)?.["tracking_number"] as string | null) ?? null;
  const { data: tracking } = useQuery({
    queryKey: ["tracking", trackingNumber],
    queryFn: () => fetchTracking({ data: { tracking: trackingNumber as string } }),
    enabled: Boolean(trackingNumber),
    refetchInterval: 60_000,
  });


  const refresh = () => qc.invalidateQueries({ queryKey: ["order", id] });
  const run = (fn: () => Promise<unknown>, ok: string) =>
    fn()
      .then(() => {
        toast.success(ok);
        refresh();
      })
      .catch((e: Error) => toast.error(e.message));

  if (isLoading) {
    return <main className="mx-auto w-full max-w-4xl px-4 py-16 text-muted-foreground sm:px-6 lg:px-8">Laddar ordern…</main>;
  }
  if (error || !data) {
    return (
      <main className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="font-heading text-2xl font-bold">Ordern hittades inte</h1>
        <Link to="/oversikt" className="mt-4 inline-flex items-center gap-2 text-primary">
          <ArrowLeft className="h-4 w-4" /> Till översikten
        </Link>
      </main>
    );
  }

  const o = data.order as Record<string, any>;
  const isBuyer = data.role === "buyer";
  const method = o["delivery_method"] as DeliveryMethod;
  const MethodIcon = methodIcon(method);
  const steps = orderSteps(method, { paymentHeld: o["payment_held"] !== false });
  const currentIndex = Math.max(
    0,
    steps.findIndex((s) => s.status === o["status"]),
  );

  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <Link to="/oversikt" className="inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Till översikten
      </Link>

      <header className="mt-4 flex flex-wrap items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-2xl font-bold text-foreground sm:text-3xl">
          Trygg affär #{o["order_no"]}
        </h1>
        {o["paid_at"] && (
          <Link to="/kvitto/$id" params={{ id: o["id"] }} className="text-sm font-medium text-primary hover:underline">
            Kvitto
          </Link>
        )}
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone(o["status"])}`}>
          {statusLabel(o["status"])}
        </span>
        {o["is_simulated"] && (
          <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-600">
            Övning
          </span>
        )}
      </header>

      <section className="mt-6 flex gap-4 rounded-xl border border-border bg-card p-4">
        {o["ad_image"] && (
          <img
            src={adImageSrc(o["ad_image"])}
            alt={o["ad_title"]}
            className="h-20 w-20 shrink-0 rounded-lg object-cover"
            loading="lazy"
          />
        )}
        <div className="min-w-0">
          <Link to="/a/$slug" params={{ slug: o["ad_slug"] }} className="font-semibold hover:underline">
            {o["ad_title"]}
          </Link>
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <MethodIcon className="h-4 w-4" /> {deliveryLabel(method)}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {isBuyer ? "Du köper" : "Du säljer"} · {kr(o["total_amount"])}
          </p>
        </div>
      </section>

      {/* Tidslinje */}
      <ol className="mt-6 grid gap-2 sm:grid-cols-5">
        {steps.map((s, i) => {
          const done = i <= currentIndex && o["status"] !== "pending_payment";
          return (
            <li
              key={s.status}
              className={`rounded-lg border p-3 text-center text-sm ${
                done ? "border-primary bg-ocean-50 text-ocean-700" : "border-border text-muted-foreground"
              }`}
            >
              <span className="flex items-center justify-center gap-1.5 font-medium">
                {done && <Check className="h-3.5 w-3.5" />} {s.label}
              </span>
            </li>
          );
        })}
      </ol>

      {/* Belopp */}
      <dl className="mt-6 rounded-xl border border-border bg-card p-4 text-sm">
        <div className="flex justify-between py-1">
          <dt>Pryl</dt>
          <dd>{kr(o["item_amount"])}</dd>
        </div>
        <div className="flex justify-between py-1">
          <dt>Frakt</dt>
          <dd>{o["shipping_amount"] === 0 ? "0 kr" : kr(o["shipping_amount"])}</dd>
        </div>
        <div className="flex justify-between py-1">
          <dt>Serviceavgift</dt>
          <dd>{kr(o["fee_amount"])}</dd>
        </div>
        <div className="mt-2 flex justify-between border-t border-border pt-2 font-semibold">
          <dt>{isBuyer ? "Du betalar" : "Du får utbetalt"}</dt>
          <dd>{isBuyer ? kr(o["total_amount"]) : kr(o["payout_amount"])}</dd>
        </div>
      </dl>

      {/* Åtgärder */}
      <section className="mt-6 space-y-3 rounded-xl border border-ocean-200 bg-ocean-50/60 p-4">
        {isBuyer && o["status"] === "pending_payment" && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => run(() => pay({ data: { id } }), "Betalningen är genomförd.")}>
              {o["is_simulated"] ? "Simulera betalning" : "Betala"} {kr(o["total_amount"])}
            </Button>
            <Button
              variant="outline"
              onClick={() => run(() => cancel({ data: { id } }), "Ordern avbröts.")}
            >
              Avbryt
            </Button>
          </div>
        )}

        {!isBuyer && o["status"] === "paid" && method !== "pickup" && (
          <Button onClick={() => run(() => label({ data: { id } }), "Fraktsedeln är skapad.")}>
            <Package className="mr-2 h-4 w-4" /> Skapa fraktsedel
          </Button>
        )}

        {!isBuyer && o["status"] === "paid" && method === "pickup" && (
          <Button
            onClick={() =>
              run(() => advance({ data: { id, action: "accept_pickup" } }), "Hämtning godkänd.")
            }
          >
            Godkänn hämtning på plats
          </Button>
        )}

        {!isBuyer && o["status"] === "label_created" && (
          <Button
            onClick={() => run(() => advance({ data: { id, action: "shipped" } }), "Markerad som inlämnad.")}
          >
            Jag har lämnat in paketet
          </Button>
        )}

        {!isBuyer && o["status"] === "awaiting_pickup" && (
          <Button
            onClick={() =>
              run(() => advance({ data: { id, action: "handed_over" } }), "Markerad som överlämnad.")
            }
          >
            Prylen är överlämnad
          </Button>
        )}

        {isBuyer && ["in_transit", "delivered", "awaiting_pickup"].includes(o["status"]) && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => run(() => approve({ data: { id } }), "Tack! Säljaren får betalt.")}>
              <Check className="mr-2 h-4 w-4" /> Jag har fått prylen – godkänn
            </Button>
          </div>
        )}

        {o["status"] === "released" && (
          <p className="text-sm font-medium text-emerald-600">
            Affären är klar. {isBuyer ? "Tack för köpet!" : "Pengarna är på väg till dig."}
          </p>
        )}
        {o["status"] === "disputed" && (
          <p className="text-sm text-destructive">
            Ärendet ligger hos vår support. Se rutan längre ner för att svara eller lägga till bilder.
          </p>
        )}
        {o["status"] === "pending_payment" && !isBuyer && (
          <p className="text-sm text-muted-foreground">Väntar på att köparen betalar.</p>
        )}
      </section>

      {/* Fraktsedel */}
      {o["label_qr"] && (
        <section className="mt-6 rounded-xl border border-border bg-card p-4">
          <h2 className="font-heading text-lg font-semibold">Fraktsedel</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {isBuyer
              ? "Så här spårar du paketet."
              : "Visa koden hos ombudet, eller vid upphämtning av budbilen."}
          </p>
          <div className="mt-3">
            <SimulatedQr value={o["label_qr"]} />
          </div>
          {o["tracking_number"] && (
            <p className="mt-3 text-sm">
              <span className="text-muted-foreground">Kollinummer: </span>
              <span className="font-mono">{o["tracking_number"]}</span>{" "}
              <Link
                to="/order-sparning/$tracking"
                params={{ tracking: o["tracking_number"] }}
                className="text-primary hover:underline"
              >
                spåra paketet
              </Link>
            </p>
          )}
        </section>
      )}

      {/* Spårning */}
      {tracking?.found && tracking.events.length > 0 && (
        <section className="mt-6 rounded-xl border border-border bg-card p-4">
          <h2 className="font-heading text-lg font-semibold">Var är paketet</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {tracking.events.map((e) => (
              <li key={`${e.status}-${e.occurred_at}`} className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium">{shipmentStatusLabel(e.status)}</span>
                <span className="text-muted-foreground">
                  {new Date(e.occurred_at).toLocaleString("sv-SE")}
                  {e.place && ` · ${e.place}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}


      {/* Adress och meddelande */}
      <section className="mt-6 rounded-xl border border-border bg-card p-4 text-sm">
        <h2 className="font-heading text-lg font-semibold">Uppgifter</h2>
        <p className="mt-2">
          <span className="text-muted-foreground">Köpare: </span>
          {o["buyer_name"] || "–"}
        </p>
        {o["buyer_address"] && (
          <p>
            <span className="text-muted-foreground">Adress: </span>
            {o["buyer_address"]}
          </p>
        )}
        {o["buyer_note"] && (
          <p className="mt-1">
            <span className="text-muted-foreground">Meddelande: </span>
            {o["buyer_note"]}
          </p>
        )}
        <p className="mt-2 text-muted-foreground">
          Fortsätt gärna prata i{" "}
          <Link to="/meddelanden" className="text-primary hover:underline">
            meddelanden
          </Link>
          .
        </p>
      </section>

      <DisputePanel
        orderId={id}
        role={isBuyer ? "buyer" : "seller"}
        orderStatus={o["status"]}
        onChanged={refresh}
      />

      {/* Händelselogg */}
      <section className="mt-6 rounded-xl border border-border bg-card p-4">
        <h2 className="font-heading text-lg font-semibold">Vad som hänt</h2>
        <ul className="mt-3 space-y-2 text-sm">
          {(data.events as any[]).map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium">{statusLabel(e.status)}</span>
              <span className="text-muted-foreground">
                {new Date(e.created_at).toLocaleString("sv-SE")}
              </span>
              {e.note && <span className="w-full text-muted-foreground">{e.note}</span>}
            </li>
          ))}
        </ul>
      </section>

    </main>
  );
}
